import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Utensils, Users, Key } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Toast from './Toast';
import '../styles/UnifiedAuth.css';
import { API_URL } from '../api/config';

export default function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [restaurantName, setRestaurantName] = useState('');
  const [outletName, setOutletName] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [restaurantId, setRestaurantId] = useState('');
  const [emailError, setEmailError] = useState('');
  const navigate = useNavigate();
  const { signIn, signOut } = useAuth();
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const closeToast = () => {
    setToast({ ...toast, show: false });
  };

  const validateEmail = (text) => {
    setEmail(text);
    if (emailError) setEmailError('');
    if (text.length === 0) return;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(text)) {
      setEmailError('Please enter a valid email address with proper domain (e.g. .com, .in)');
    }
  };

  const handleSignUp = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address with proper domain (e.g. .com, .in)');
      return;
    }
    
    if (!email || !password || !restaurantName || !outletName) {
      showToast('Please fill in all fields', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/signup`, {
        email,
        password,
        restaurantName,
        outletName
      });

      if (response.data.success) {
        setRestaurantId(response.data.restaurantId);
        setShowOtpInput(true);
        showToast('Please check your email for the OTP verification code');
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'An error occurred during registration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp) {
      showToast('Please enter the OTP', 'error');
      return;
    }
    try {
      setLoading(true);
      const verifyResponse = await axios.post(`${API_URL}/verify-otp`, {
        restaurantId,
        otp
      });

      if (verifyResponse.data.success) {
        showToast('Account verified successfully');
        setTimeout(() => navigate('/signin'), 1500);
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'OTP verification failed', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="unified-auth-container">
      {toast.show && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}
      
      <div className="auth-form-wrapper">
        <h1 style={{ textAlign: 'center', color: '#8B4513', marginBottom: '5px' }}>TOBO</h1>
        <p style={{ textAlign: 'center', color: '#CD853F', marginBottom: '20px' }}>BILL IT, DONE!</p>

        <p style={{ textAlign: 'center', marginBottom: '20px' }}>
          {showOtpInput ? 'Enter OTP sent to your email' : 'Create your restaurant partner account'}
        </p>

        {!showOtpInput ? (
          <>
            <div className="form-group">
              <div style={{ position: 'relative' }}>
                <Mail style={{ position: 'absolute', left: '12px', top: '12px', color: '#8B4513' }} size={20} />
                <input
                  style={{ paddingLeft: '40px' }}
                  placeholder="Enter your email address"
                  type="email"
                  value={email}
                  onChange={(e) => validateEmail(e.target.value)}
                  autoCapitalize="none"
                />
              </div>
              {emailError && (
                <p className="error-message">{emailError}</p>
              )}
            </div>

            <div className="form-group">
              <div style={{ position: 'relative' }}>
                <Utensils style={{ position: 'absolute', left: '12px', top: '12px', color: '#8B4513' }} size={20} />
                <input
                  style={{ paddingLeft: '40px' }}
                  placeholder="Restaurant Name"
                  value={restaurantName}
                  onChange={(e) => setRestaurantName(e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <div style={{ position: 'relative' }}>
                <Users style={{ position: 'absolute', left: '12px', top: '12px', color: '#8B4513' }} size={20} />
                <input
                  style={{ paddingLeft: '40px' }}
                  placeholder="Outlet Name"
                  value={outletName}
                  onChange={(e) => setOutletName(e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <div style={{ position: 'relative' }}>
                <Lock style={{ position: 'absolute', left: '12px', top: '12px', color: '#8B4513' }} size={20} />
                <input
                  style={{ paddingLeft: '40px' }}
                  placeholder="Create Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <button
              className="submit-btn"
              onClick={handleSignUp}
              disabled={loading || emailError}
              style={{ opacity: loading || emailError ? 0.7 : 1 }}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </>
        ) : (
          <>
            <div className="form-group">
              <div style={{ position: 'relative' }}>
                <Key style={{ position: 'absolute', left: '12px', top: '12px', color: '#8B4513' }} size={20} />
                <input
                  style={{ paddingLeft: '40px' }}
                  placeholder="Enter OTP"
                  type="number"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                />
              </div>
            </div>

            <button
              className="submit-btn"
              onClick={handleVerifyOTP}
              disabled={loading}
              style={{ opacity: loading ? 0.7 : 1 }}
            >
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
          </>
        )}

<div className="auth-links" style={{ justifyContent: 'center' }}>
  <span onClick={() => {
    signOut();
    navigate('/signin');
  }}>
    Already have an account? Login
  </span>
</div>


        <div style={{ marginTop: '40px', textAlign: 'center' }}>
          <p style={{ color: '#666', fontSize: '14px' }}>By continuing, you agree to our</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
  <a 
    href="https://ocena.in/terms&condition" 
    target="_blank" 
    rel="noopener noreferrer" 
    style={{ color: '#8B4513', fontSize: '14px', textDecoration: 'none' }}
  >
    Terms & Conditions
  </a>
  <span style={{ color: '#666' }}>|</span>
  <a 
    href="https://ocena.in/privacy" 
    target="_blank" 
    rel="noopener noreferrer" 
    style={{ color: '#8B4513', fontSize: '14px', textDecoration: 'none' }}
  >
    Privacy Policy
  </a>
</div>

        </div>
      </div>
    </div>
  );
}